import { Dispatch, FC, SetStateAction, useState } from 'react';
import Button from '../../components/atoms/Button/Button';
import Container from '../../components/atoms/Container/Container';

import Field from '../../components/atoms/Field/Field';
import {
  getCardNumberMask,
  getCurrencySymbol,
} from '../../services/utils';
import MerchantBrandingBlock from '../../components/organisms/MerchantBrandingBlock/MerchantBrandingBlock';
import { Transaction } from '../../services/types';
import card from '../../assets/images/card.svg';
import { useBankLogo } from '../../utils/bankLogos';
import InfoBlock from '../../components/organisms/InfoBlock/InfoBlock';
import Header from '../../components/organisms/Header/Header';
import Labeled from '../../components/atoms/Labeled/Labeled';
import SupportButton from '../../components/atoms/SupportButton/SupportButton';
import styles from './A2APage.module.scss';
import RowFields from '../../components/atoms/RowFields/RowFields';
import AmountAlert from '../../components/organisms/AmountAlert/AmountAlert';
import { showsAmountChangeNotice } from '../../services/amountChange';
import type { DeclineContext } from '../../services/types';
import { ButtonGroup } from '../../components/ButtonGroup/ButtonGroup';

interface A2APageProps {
  transaction: Transaction;
  timeLeft: string;
  ticketId: string;
  onCancel: () => void;
  /** Cancel as a refusal of the adjusted amount. */
  onDeclineOffer: (context: DeclineContext) => Promise<void> | void;
  onConfirm: () => void;
  setShowConfirm: Dispatch<SetStateAction<boolean>>;
}

const A2APage: FC<A2APageProps> = ({
  transaction,
  ticketId,
  onCancel,
  onDeclineOffer,
  onConfirm,
  timeLeft,
  setShowConfirm,
}) => {
  const { requisite, isRedirectUrlExist, amount } = transaction;
  const currencySymbol = getCurrencySymbol(requisite.currency);
  const { src: bankLogoSrc, hasLogo: hasBankLogo } = useBankLogo(requisite?.acquiringBank);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleConfirmClick = async () => {
    if (isProcessing) return;
    if (transaction?.simpleConfirmation === false) {
      setShowConfirm(true);
      return;
    }
    setIsProcessing(true);
    try {
      await onConfirm();
    } finally {
      setIsProcessing(false);
    }
  };

  const requesiteTitle = (type: string | undefined) => {
    switch (type) {
      case 'bank_account':
        return 'Номер счета';
      case 'bank_card':
        return 'Номер карты';
      case 'crypto_wallet':
        return 'Номер кошелька';
      case 'phone_number':
        return 'Номер телефона';
      default:
        return 'Номер реквизита';
    }
  };

  return (
    <>
      <Header
        transaction={transaction}
        ticketId={ticketId}
        timeLeft={timeLeft}
      />

      {transaction.merchant.toLowerCase() === 'vocxod' && (
        <div style={{ marginBottom: '28px' }}>
          <SupportButton userId={transaction.externalUserId} />
        </div>
      )}
      <Container>
        <MerchantBrandingBlock
          merchant={transaction.merchant}
          className={styles.imageWrapper}
        />
        <div className={styles.fieldGroup}>
          <Field image={card} text={'По номеру счета'} bold />

          <div>
            <RowFields direction={requisite.qrCodeData ? 'column' : 'row'}>
              {amount && (
                <Labeled width='100%'>
                  <span>Сумма перевода</span>
                  <Field text={amount} copy copyField='amount' currency={currencySymbol} />
                </Labeled>
              )}

              {requisite?.acquiringBank && (
                <Labeled width='100%'>
                  <span>Банк</span>
                  {hasBankLogo ? (
                    <Field image={bankLogoSrc} />
                  ) : (
                    <Field
                      text={
                        requisite?.acquiringBank.length > 15
                          ? requisite?.acquiringBank
                              ?.toUpperCase()
                              .slice(0, 17) + '...'
                          : requisite?.acquiringBank?.toUpperCase()
                      }
                    />
                  )}
                </Labeled>
              )}
            </RowFields>
          </div>
          {!isRedirectUrlExist && (
            <Labeled>
              <span>{requesiteTitle(transaction.requisite.type)}</span>
              <Field
                copy
                copyType='clean'
                copyField='requisite'
                text={getCardNumberMask(transaction?.requisite?.accountNumber)}
              />
            </Labeled>
          )}
          {transaction?.requisite?.country &&
            transaction?.requisite?.country !== ' ' && (
              <Labeled>
                <span>Страна</span>
                <Field text={transaction?.requisite?.country} />
              </Labeled>
            )}
          {transaction?.requisite?.name && (
            <Labeled>
              <span>Получатель</span>
              <Field text={transaction?.requisite?.name} />
            </Labeled>
          )}
          {transaction?.simpleConfirmation === false && (
            <Labeled>
              <span>Инструкция</span>
              <InfoBlock data='' confirm={true} merchant={transaction?.merchant} />
            </Labeled>
          )}
          {transaction?.requisite?.userManual && (
            <Labeled>
              <span>Инструкция</span>
              <InfoBlock data={transaction?.requisite?.userManual} />
            </Labeled>
          )}
          <ButtonGroup>
            <Button
              variant='secondary'
              onClick={onCancel}
              disabled={isProcessing}
              dataSentryElement='cancel-ticket'
            >
              Отмена
            </Button>
            <Button
              onClick={handleConfirmClick}
              variant='primary'
              disabled={isProcessing}
              dataSentryElement='paid'
            >
              {isProcessing
                ? 'Проверяем платёж…'
                : isRedirectUrlExist
                ? 'Перейти к оплате'
                : 'Далее'}
            </Button>
          </ButtonGroup>
        </div>
        {showsAmountChangeNotice(transaction) && (
          <AmountAlert
            transaction={transaction}
            onDecline={() => onDeclineOffer('amount_change')}
          />
        )}
      </Container>
    </>
  );
};

export default A2APage;
